/**
 * Shared North-American phone-number formatting, used everywhere a raw
 * caller-ID number would otherwise reach the screen unformatted (owner
 * morning review G3: JourneyFlow, journeyNarrative, InteractionDetail's top
 * block, and CallList all had their own copy or none at all).
 */

/**
 * Formats a 10-digit (or +1-prefixed 11-digit) North American number as
 * "(778) 381-5460". Any other shape (already formatted, international, an
 * extension, garbage) is returned unchanged rather than mangled. `null` /
 * `undefined` becomes `'-'`, matching the two pre-existing local copies this
 * replaces (`CallList.tsx`, `dashboard/MyDashboard.tsx`).
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) {
    return '-';
  }

  const digitsOnly = phone.replace(/\D/g, '');
  const tenDigit =
    digitsOnly.length === 11 && digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;

  if (tenDigit.length === 10) {
    return `(${tenDigit.slice(0, 3)}) ${tenDigit.slice(3, 6)}-${tenDigit.slice(6)}`;
  }

  return phone;
}

/** True when `value` reads as a phone number (mostly digits) rather than a human/business name. */
export function isPhoneLikeLabel(value: string): boolean {
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.length >= 7 && digitsOnly.length <= 15;
}

/**
 * Combines a caller-ID / display name with its number for one line of UI or
 * narrative text, e.g. "TERRY FOX FDTN (450) 923-8950". `formatPhoneNumber`
 * already wraps the area code in parentheses, so the name and number are
 * just space-joined here -- wrapping the whole number in a second pair
 * would double up parens ("Person B ((555) 000-0002)"). Falls back to just
 * the formatted number when there is no name, or when the "name" is itself
 * just the number (some carriers put the digits in the display-name field).
 */
export function formatPartyLabel(
  name: string | null | undefined,
  number: string | null | undefined,
): string {
  const trimmedName = name?.trim();
  const formattedNumber = formatPhoneNumber(number);

  if (!trimmedName || isPhoneLikeLabel(trimmedName)) {
    return formattedNumber;
  }

  return `${trimmedName} ${formattedNumber}`;
}
